/* ================================================================
   SS BIGWIN — App JS
   Vanilla JS. No frameworks. ~150 lines.
   Handles: scroll reveals, sticky header, floating book bar,
   counter animation, FAQ accordion, testimonial carousel, gallery lightbox.
   ================================================================ */

(function () {
  'use strict';

  /* ──────────────────────────────────────────────────────────────
     CONFIG — timing, counter base, and animation constants.
     ────────────────────────────────────────────────────────────── */
  var TIMING = {
    CAROUSEL_INTERVAL_MS: 8000,    /* testimonial auto-advance */
    COUNTER_DURATION_MS: 1800      /* stat counter animation length */
  };
  var COUNTER = {
    LIVE_BASE_DATE: '2025-06-01',  /* baseline anchor for live passenger count */
    /* Live passenger count grows 15-92 passengers per day during summer months
       (June–October). Seeded from the date for day-to-day consistency. */
    SUMMER_START_MONTH: 5,         /* 0-indexed: June */
    SUMMER_END_MONTH: 9,           /* 0-indexed: October */
    DAILY_MIN: 15,
    DAILY_MAX: 92
  };

  /* ── Scroll Reveal ── */
  function initReveal() {
    var els = document.querySelectorAll('.ssbw-reveal');
    if (!els.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ── Sticky Header ── */
  function initStickyHeader() {
    var header = document.getElementById('ssbw-header');
    if (!header) return;
    var hero = document.querySelector('[data-ssbw-hero]');
    if (!hero) {
      header.classList.add('is-solid');
      return;
    }
    header.classList.add('is-transparent');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          header.classList.add('is-transparent');
          header.classList.remove('is-solid');
        } else {
          header.classList.remove('is-transparent');
          header.classList.add('is-solid');
        }
      });
    }, { threshold: 0, rootMargin: '-80px 0px 0px 0px' });
    io.observe(hero);
  }

  /* ── Floating Book Bar ── */
  function initFloatingBar() {
    var bar = document.getElementById('ssbw-floating-book');
    if (!bar) return;

    /* On the Cruises page, show bar when booking widget scrolls out of view.
       On other pages, show when hero scrolls out (or immediately if no hero). */
    var booking = document.querySelector('[data-ssbw-booking]');
    var trigger = booking || document.querySelector('[data-ssbw-hero]');
    if (!trigger) { bar.classList.add('is-visible'); return; }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!bar.classList.contains('is-dismissed')) {
          bar.classList.toggle('is-visible', !e.isIntersecting);
        }
      });
    }, { threshold: 0 });
    io.observe(trigger);

    var close = bar.querySelector('[data-ssbw-float-close]');
    if (close) {
      close.addEventListener('click', function () {
        bar.classList.add('is-dismissed');
        bar.classList.remove('is-visible');
      });
    }
  }

  /* ── Counter Animation ── */
  function commaFormat(n) {
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* Live passenger counter. See COUNTER constants above for tuning. */
  function getLiveCount(base, baseDate) {
    var now = new Date();
    var d = new Date(baseDate);
    var total = base;
    var range = COUNTER.DAILY_MAX - COUNTER.DAILY_MIN + 1;
    while (d < now) {
      var m = d.getMonth();
      if (m >= COUNTER.SUMMER_START_MONTH && m <= COUNTER.SUMMER_END_MONTH) {
        var seed = d.getFullYear() * 10000 + (m + 1) * 100 + d.getDate();
        var pseudo = ((seed * 9301 + 49297) % 233280) / 233280;
        total += Math.floor(pseudo * range) + COUNTER.DAILY_MIN;
      }
      d.setDate(d.getDate() + 1);
    }
    return total;
  }

  function initCounters() {
    var counters = document.querySelectorAll('[data-ssbw-counter]');
    if (!counters.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        var raw = parseInt(el.getAttribute('data-ssbw-counter'), 10);
        if (isNaN(raw)) return;
        io.unobserve(el);

        /* If this is the live passenger counter, calculate the real number */
        var target = raw;
        var isLive = el.hasAttribute('data-ssbw-counter-live');
        if (isLive) {
          target = getLiveCount(raw, COUNTER.LIVE_BASE_DATE);
        }

        var start = 0;
        var duration = TIMING.COUNTER_DURATION_MS;
        var t0 = null;
        function step(ts) {
          if (!t0) t0 = ts;
          var p = Math.min((ts - t0) / duration, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          var val = Math.round(start + (target - start) * eased);
          el.textContent = (target >= 1000 && target <= 2099 && !isLive) ? val : commaFormat(val);
          if (p < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      });
    }, { threshold: 0.3 });
    counters.forEach(function (el) { io.observe(el); });
  }

  /* ── FAQ Accordion ── */
  function initFAQ() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-ssbw-faq-trigger]');
      if (!trigger) return;
      var item = trigger.closest('.ssbw-faq__item');
      if (!item) return;
      var isOpen = item.classList.contains('is-open');
      // Close siblings (and sync their trigger aria state)
      var parent = item.parentElement;
      if (parent) {
        parent.querySelectorAll('.ssbw-faq__item.is-open').forEach(function (sib) {
          sib.classList.remove('is-open');
          var sibTrigger = sib.querySelector('[data-ssbw-faq-trigger]');
          if (sibTrigger) sibTrigger.setAttribute('aria-expanded', 'false');
        });
      }
      if (!isOpen) item.classList.add('is-open');
      trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });
  }

  /* ── Testimonial Carousel ── */
  function initCarousels() {
    document.querySelectorAll('[data-ssbw-carousel]').forEach(function (carousel) {
      var track = carousel.querySelector('[data-ssbw-carousel-track]');
      var dots = carousel.querySelectorAll('[data-ssbw-carousel-dot]');
      var slides = track ? track.children : [];
      if (slides.length < 2) return;
      var current = 0;
      var interval = null;

      /* Initialize: mark first slide active */
      if (slides.length > 0) slides[0].classList.add('is-active');

      function goTo(i) {
        current = ((i % slides.length) + slides.length) % slides.length;
        for (var s = 0; s < slides.length; s++) {
          slides[s].classList.toggle('is-active', s === current);
        }
        dots.forEach(function (d, idx) {
          d.classList.toggle('is-active', idx === current);
        });
      }

      function startAuto() {
        /* Idempotent: dot-click + mouseleave can both call this — without the
           clear, two intervals stack and the carousel advances at 2x speed. */
        clearInterval(interval);
        interval = setInterval(function () { goTo(current + 1); }, TIMING.CAROUSEL_INTERVAL_MS);
      }

      function stopAuto() { clearInterval(interval); }

      dots.forEach(function (dot, idx) {
        dot.addEventListener('click', function () {
          stopAuto();
          goTo(idx);
          startAuto();
        });
      });

      carousel.addEventListener('mouseenter', stopAuto);
      carousel.addEventListener('mouseleave', startAuto);
      startAuto();
    });
  }

  /* ── Gallery Lightbox ── */
  function initLightbox() {
    var box = document.getElementById('ssbw-lightbox');
    if (!box) return;
    var img = box.querySelector('[data-ssbw-lightbox-img]');
    var caption = box.querySelector('[data-ssbw-lightbox-caption]');
    var items = document.querySelectorAll('[data-ssbw-lightbox-trigger]');
    var current = 0;

    function open(i) {
      current = i;
      var trigger = items[i];
      img.src = trigger.getAttribute('data-ssbw-lightbox-src') || trigger.src;
      img.alt = trigger.alt || '';
      var cap = trigger.getAttribute('data-ssbw-lightbox-caption') || '';
      if (caption) caption.textContent = cap;
      box.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    function close() {
      box.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    function nav(dir) {
      current = ((current + dir) % items.length + items.length) % items.length;
      open(current);
    }

    items.forEach(function (el, i) {
      el.addEventListener('click', function (e) { e.preventDefault(); open(i); });
    });

    box.querySelector('[data-ssbw-lightbox-close]')?.addEventListener('click', close);
    box.querySelector('[data-ssbw-lightbox-prev]')?.addEventListener('click', function () { nav(-1); });
    box.querySelector('[data-ssbw-lightbox-next]')?.addEventListener('click', function () { nav(1); });
    box.addEventListener('click', function (e) { if (e.target === box) close(); });

    document.addEventListener('keydown', function (e) {
      if (!box.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') nav(-1);
      if (e.key === 'ArrowRight') nav(1);
    });
  }

  /* ── Hero Stagger Reveal ── */
  function initHeroStagger() {
    var hero = document.querySelector('[data-ssbw-hero]');
    if (!hero) return;
    var items = hero.querySelectorAll('.ssbw-hero__stagger');
    items.forEach(function (el, i) {
      el.style.animationDelay = (0.3 + i * 0.15) + 's';
    });
  }

  /* ── Interactive Timeline ── */
  function initInteractiveTimeline() {
    document.querySelectorAll('[data-ssbw-itimeline]').forEach(function (section) {
      var tabs = section.querySelectorAll('[data-ssbw-itimeline-tab]');
      var panels = section.querySelectorAll('[data-ssbw-itimeline-panel]');
      var progress = section.querySelector('.ssbw-itimeline__progress');
      if (!tabs.length || !panels.length) return;

      function activate(index) {
        tabs.forEach(function (t, i) {
          var active = i === index;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
          t.setAttribute('tabindex', active ? '0' : '-1');
        });
        panels.forEach(function (p, i) {
          p.classList.toggle('is-active', i === index);
        });
        if (progress && tabs.length > 1) {
          var pct = (index / (tabs.length - 1)) * 100;
          progress.style.width = pct + '%';
        }
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { activate(i); });
        tab.addEventListener('keydown', function (e) {
          var idx = -1;
          if (e.key === 'ArrowRight') idx = (i + 1) % tabs.length;
          if (e.key === 'ArrowLeft') idx = (i - 1 + tabs.length) % tabs.length;
          if (e.key === 'Home') idx = 0;
          if (e.key === 'End') idx = tabs.length - 1;
          if (idx >= 0) {
            e.preventDefault();
            activate(idx);
            tabs[idx].focus();
          }
        });
      });
    });
  }

  /* ── Ship Explorer ── */
  function initShipExplorer() {
    document.querySelectorAll('[data-ssbw-ship-explorer]').forEach(function (section) {
      var hotspots = section.querySelectorAll('[data-ssbw-hotspot]');
      var detail = section.querySelector('[data-ssbw-ship-detail]');
      var closeBtn = section.querySelector('[data-ssbw-ship-detail-close]');
      if (!hotspots.length || !detail) return;

      var detailImg = detail.querySelector('[data-ssbw-ship-detail-img]');
      var detailKicker = detail.querySelector('[data-ssbw-ship-detail-kicker]');
      var detailTitle = detail.querySelector('[data-ssbw-ship-detail-title]');
      var detailText = detail.querySelector('[data-ssbw-ship-detail-text]');

      function openDetail(hs) {
        hotspots.forEach(function (h) { h.classList.remove('is-active'); });
        hs.classList.add('is-active');

        if (detailImg) {
          var imgSrc = hs.getAttribute('data-ssbw-hotspot-img');
          /* Build the <img> via DOM (no innerHTML string-concat) and only accept
             http(s)/relative src — defense-in-depth against a malformed admin-set URL. */
          detailImg.textContent = '';
          if (imgSrc && /^(https?:|\/)/.test(imgSrc)) {
            var hsImg = document.createElement('img');
            hsImg.setAttribute('src', imgSrc);
            hsImg.setAttribute('alt', '');
            hsImg.setAttribute('loading', 'lazy');
            detailImg.appendChild(hsImg);
            detailImg.style.display = '';
          } else {
            detailImg.style.display = 'none';
          }
        }
        if (detailKicker) detailKicker.textContent = hs.getAttribute('data-ssbw-hotspot-kicker') || '';
        if (detailTitle) detailTitle.textContent = hs.getAttribute('data-ssbw-hotspot-title') || '';
        if (detailText) detailText.textContent = hs.getAttribute('data-ssbw-hotspot-text') || '';

        detail.classList.add('is-open');
      }

      function closeDetail() {
        detail.classList.remove('is-open');
        hotspots.forEach(function (h) { h.classList.remove('is-active'); });
      }

      hotspots.forEach(function (hs) {
        hs.addEventListener('click', function () { openDetail(hs); });
      });

      if (closeBtn) closeBtn.addEventListener('click', closeDetail);

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && detail.classList.contains('is-open')) {
          closeDetail();
        }
      });
    });
  }

  /* ── Cart Badge ── */
  function updateCartBadge() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = 0;
        cart.items.forEach(function (item) {
          if (item.handle && item.handle.indexOf('cruise-seat-') === 0) return;
          count += item.quantity;
        });
        document.querySelectorAll('#ssbw-cart-count, [data-cart-count]').forEach(function (el) {
          el.textContent = count;
          el.classList.toggle('ssbw-hidden', count === 0);
        });
      })
      .catch(function () {});
  }

  function initCartBadge() {
    /* Intercept fetch to detect cart changes */
    var origFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      var urlStr = typeof url === 'string' ? url : (url && url.url ? url.url : '');
      var result = origFetch.apply(this, arguments);
      if (urlStr && (urlStr.indexOf('/cart/add') !== -1 || urlStr.indexOf('/cart/change') !== -1 || urlStr.indexOf('/cart/update') !== -1 || urlStr.indexOf('/cart/clear') !== -1)) {
        result.then(function () { updateCartBadge(); });
      }
      return result;
    };
  }

  /* ── Repair-closure pop-up ── */
  /* Shows the site-wide closure notice once per browsing session (sessionStorage).
     The section only renders when global setting `repair_mode` is on. */
  function initRepairPopup() {
    var popup = document.querySelector('[data-ssbw-repair-popup]');
    if (!popup) return;
    var SEEN = 'ssbw-repair-popup-seen';
    var alreadySeen = false;
    try { alreadySeen = sessionStorage.getItem(SEEN) === '1'; } catch (e) { /* storage blocked (private mode) — show each load */ }

    function markSeen() { try { sessionStorage.setItem(SEEN, '1'); } catch (e) { /* storage blocked — ignore */ } }
    function openPopup() {
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    function closePopup() {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      markSeen();
    }

    var closers = popup.querySelectorAll('[data-ssbw-repair-close]');
    for (var i = 0; i < closers.length; i++) {
      closers[i].addEventListener('click', closePopup);
    }
    /* Backdrop click (outside the card) closes. */
    popup.addEventListener('click', function (e) { if (e.target === popup) closePopup(); });
    /* Escape closes. */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && popup.classList.contains('is-open')) closePopup();
    });
    /* CTA click-through: mark seen (so it doesn't reappear on the target page) and let it navigate. */
    var donate = popup.querySelector('.ssbw-repair-popup__cta, .ssbw-repair-popup__donate');
    if (donate) donate.addEventListener('click', markSeen);

    if (!alreadySeen) {
      /* brief delay so it doesn't fight first paint */
      setTimeout(openPopup, 700);
    }
  }

  /* ── Init All ── */
  document.addEventListener('DOMContentLoaded', function () {
    initReveal();
    initStickyHeader();
    initFloatingBar();
    initCounters();
    initFAQ();
    initCarousels();
    initLightbox();
    initHeroStagger();
    initInteractiveTimeline();
    initShipExplorer();
    initCartBadge();
    initRepairPopup();
  });

})();
